/**
 * useCallNotifications Hook
 * Handles ringtones, dial tones, haptic feedback, and native call notifications
 */

'use client';

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { isCapacitor, isAndroid } from '@/lib/platformUtils';
import { haptics } from '@/lib/haptics';
import {
  getDialtoneFilePath,
  getRingtoneFilePath,
  loadCallSoundPreferences,
  type IncomingRingtonePlayback,
} from '@/lib/callSoundPreferences';
import type { UseCallNotificationsReturn, CallType } from '../types';

// Native plugins - dynamically imported for Capacitor
type NotificationManagerBridge = {
  playRingtone(options?: { loop?: boolean }): Promise<{ success: boolean }>;
  stopPlayingRingtone(): Promise<{ success: boolean }>;
  stopRingtone(): Promise<{ success: boolean }>;
  cancelCallNotification(): Promise<{ success: boolean }>;
  showIncomingCall?(options: { callerName: string; callType: 'voice' | 'video'; timeout: number }): Promise<{ success: boolean }>;
};

let NotificationManager: NotificationManagerBridge | null = null;

// Initialize native plugins on first use
async function initializePlugins() {
  if (isCapacitor()) {
    // NotificationManager is our custom Android plugin for call notifications
    if (isAndroid()) {
      try {
        const nmModule = await import('@/plugins/NotificationManager');
        NotificationManager = nmModule.NotificationManager;
      } catch (e) {
        console.log('[useCallNotifications] NotificationManager not available');
      }
    }
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initializePlugins();
}

export function useCallNotifications(): UseCallNotificationsReturn {
  const [isRinging, setIsRinging] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  
  // Audio element refs
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const ringtoneSrcRef = useRef<string | null>(null);
  const dialToneRef = useRef<HTMLAudioElement | null>(null);
  const dialToneSrcRef = useRef<string | null>(null);
  const vibrationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  /**
   * Stop any active incoming-call haptics/vibration
   */
  const stopIncomingHaptics = useCallback(() => {
    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
      vibrationIntervalRef.current = null;
    }

    if (navigator.vibrate) {
      navigator.vibrate(0);
    }

    if (isCapacitor()) {
      void haptics.cancel();
    }
  }, []);

  /**
   * Run a single incoming-call haptic pulse
   */
  const runIncomingHapticPulse = useCallback(async () => {
    if (isCapacitor()) {
      try {
        await haptics.callIncoming();
        return;
      } catch (_e) {
        // Fall through to shared haptics helper
      }

      await haptics.medium();
      await haptics.vibrate(180);
      return;
    }

    if (navigator.vibrate) {
      navigator.vibrate([100, 100, 100, 100, 200]);
    }
  }, []);

  /**
   * Start vibration pattern for incoming calls
   */
  const startVibrationPattern = useCallback(() => {
    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
    }

    void runIncomingHapticPulse();

    vibrationIntervalRef.current = setInterval(() => {
      void runIncomingHapticPulse();
    }, 2500);
  }, [runIncomingHapticPulse]);

  /**
   * Stop native ringtone playback on Android
   */
  const stopNativeRingtone = useCallback(async () => {
    if (!isCapacitor() || !isAndroid() || !NotificationManager) {
      return;
    }

    try {
      await NotificationManager.stopPlayingRingtone();
    } catch (_e) {
      // Ignore - plugin may not be ready yet
    }

    try {
      await NotificationManager.stopRingtone();
    } catch (_e) {
      // Ignore - notification may already be gone
    }
  }, []);

  /**
   * Play incoming ringtone from Profile → Call sounds (localStorage).
   * Uses Web Audio everywhere (including Android) so preset clips match the user's selection.
   * Silent / vibrate-only: haptics only on Capacitor — avoids playing the system default tone.
   */
  const playWebRingtone = useCallback((playback?: IncomingRingtonePlayback) => {
    try {
      const prefs = loadCallSoundPreferences();
      const path = playback !== undefined ? playback.path : getRingtoneFilePath(prefs);
      const volume = playback !== undefined ? playback.volume : prefs.ringtoneVolume;

      if (!path) {
        if (isCapacitor() && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          startVibrationPattern();
        }
        return;
      }

      if (!ringtoneRef.current || ringtoneSrcRef.current !== path) {
        ringtoneRef.current?.pause();
        ringtoneRef.current = new Audio(path);
        ringtoneSrcRef.current = path;
        ringtoneRef.current.loop = true;
      }

      ringtoneRef.current.volume = Math.min(1, Math.max(0, volume));
      ringtoneRef.current.currentTime = 0;
      const playPromise = ringtoneRef.current.play();

      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          console.log('[useCallNotifications] Ringtone play failed:', e);
          setAudioBlocked(true);
        });
      }

      if (isCapacitor() && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        startVibrationPattern();
      }
    } catch (e) {
      console.error('[useCallNotifications] Error playing ringtone:', e);
    }
  }, [startVibrationPattern]);

  /**
   * Play ringtone for incoming calls
   */
  const playRingtone = useCallback((playback?: IncomingRingtonePlayback) => {
    console.log('[useCallNotifications] Playing ringtone');
    setAudioBlocked(false);
    setIsRinging(true);
    playWebRingtone(playback);
  }, [playWebRingtone]);
  
  /**
   * Stop ringtone and vibration
   */
  const stopRingtone = useCallback(() => {
    console.log('[useCallNotifications] Stopping ringtone');
    setIsRinging(false);
    setAudioBlocked(false);
    
    // Stop web audio
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    
    void stopNativeRingtone();
    stopIncomingHaptics();
  }, [stopIncomingHaptics, stopNativeRingtone]);
  
  /**
   * Play dial tone from Profile → Call sounds (outgoing dial tone preference).
   */
  const playDialTone = useCallback(() => {
    console.log('[useCallNotifications] Playing dial tone');

    try {
      const prefs = loadCallSoundPreferences();
      const path = getDialtoneFilePath(prefs);
      const volume = prefs.dialToneVolume;

      if (!path) {
        return;
      }

      if (!dialToneRef.current || dialToneSrcRef.current !== path) {
        dialToneRef.current?.pause();
        dialToneRef.current = new Audio(path);
        dialToneSrcRef.current = path;
        dialToneRef.current.loop = true;
      }

      dialToneRef.current.volume = Math.min(1, Math.max(0, volume));
      dialToneRef.current.currentTime = 0;
      dialToneRef.current.play().catch((e) => {
        console.log('[useCallNotifications] Dial tone play failed:', e);
      });
    } catch (e) {
      console.error('[useCallNotifications] Error playing dial tone:', e);
    }
  }, []);
  
  /**
   * Stop dial tone
   */
  const stopDialTone = useCallback(() => {
    console.log('[useCallNotifications] Stopping dial tone');
    
    if (dialToneRef.current) {
      dialToneRef.current.pause();
      dialToneRef.current.currentTime = 0;
    }
  }, []);
  
  /**
   * Trigger haptic feedback
   */
  const hapticFeedback = useCallback((style: 'light' | 'medium' | 'heavy') => {
    if (!isCapacitor()) return;
    void haptics[style]();
  }, []);
  
  /**
   * Cancel any active call notification
   */
  const cancelCallNotification = useCallback(async () => {
    console.log('[useCallNotifications] Cancelling call notification');

    await stopNativeRingtone();
    
    // Cancel Android call notification
    if (isCapacitor() && isAndroid() && NotificationManager) {
      try {
        await NotificationManager.cancelCallNotification();
      } catch (e) {
        console.log('[useCallNotifications] Cancel notification error:', e);
      }
    }
    
    // Stop any playing audio
    stopRingtone();
    stopDialTone();
  }, [stopDialTone, stopNativeRingtone, stopRingtone]);
  
  /**
   * Show incoming call notification (Android only)
   */
  const showCallNotification = useCallback(async (callerName: string, callType: CallType) => {
    console.log('[useCallNotifications] Showing call notification for:', callerName);
    
    if (!isCapacitor() || !isAndroid() || !NotificationManager) {
      return;
    }
    
    try {
      if (!NotificationManager.showIncomingCall) {
        return;
      }

      await NotificationManager.showIncomingCall({
        callerName,
        callType: callType === 'video' ? 'video' : 'voice',
        timeout: 45000, // 45 second timeout
      });
    } catch (e) {
      console.error('[useCallNotifications] Show notification error:', e);
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current = null;
      }
      ringtoneSrcRef.current = null;
      if (dialToneRef.current) {
        dialToneRef.current.pause();
        dialToneRef.current = null;
      }
      dialToneSrcRef.current = null;
      stopIncomingHaptics();
      void stopNativeRingtone();
    };
  }, [stopIncomingHaptics, stopNativeRingtone]);
  
  // Memoize return value to prevent unnecessary re-renders
  return useMemo(() => ({
    isRinging,
    audioBlocked,
    playRingtone,
    stopRingtone,
    playDialTone,
    stopDialTone,
    hapticFeedback,
    cancelCallNotification,
    showCallNotification,
  }), [isRinging, audioBlocked, playRingtone, stopRingtone, playDialTone, stopDialTone, hapticFeedback, cancelCallNotification, showCallNotification]);
}

export default useCallNotifications;
